import {
  backButton,
  init,
  mainButton,
  miniApp,
  retrieveRawInitData,
  secondaryButton,
  settingsButton,
  swipeBehavior,
  themeParams,
  viewport,
} from '@tma.js/sdk-react'
import { bindColorScheme } from './appearance'
import { bindBackButton } from './back-button'
import { bindSettingsButton } from './settings-button'

export type LaunchContext = {
  /** Сырая подписанная строка — уходит на бэкенд заголовком, больше никуда. */
  rawInitData: string | null
  /** `language_code` пользователя Telegram: язык по умолчанию. */
  languageCode: string | null
  /** `startapp`-параметр ссылки: из чата сюда приходит идентификатор подарка. */
  startParam: string | null
  /**
   * Telegram ID из `initData` — без проверки подписи (её проверяет бэкенд), а
   * потому только ключ данных на этом устройстве: чей офлайн-ваучер показывать
   * и чьи стирать. Ни в запросы, ни в логи не уходит. Есть и без сети: подпись
   * клиент Telegram передаёт при запуске.
   */
  telegramId: number | null
}

/**
 * Кто открыл Mini App — для аватара в шапке каталога (CLAUDE.md, «Профиль»):
 * фото из `photo_url`, иначе инициалы. Без проверки подписи и только для
 * вида: ни в запросы, ни в логи не уходит.
 */
export type TelegramUser = {
  firstName: string
  lastName: string | null
  /** Есть не у всех: фото скрыто настройками приватности или клиент его не передал. */
  photoUrl: string | null
  /** Имя пользователя Telegram без «@» — строка под именем в профиле; есть не у всех. */
  username: string | null
}

let telegramUser: TelegramUser | null = null

let launchTelegramId: number | null = null

/** Пользователь из `initData` текущего запуска; `null` — вне Telegram без подмены или повреждённый `user`. */
export const getTelegramUser = (): TelegramUser | null => telegramUser

/**
 * Telegram ID текущего запуска (`LaunchContext.telegramId`) — только ключ данных на
 * этом устройстве (флаги подсказок); ни в запросы, ни в логи не уходит.
 */
export const getTelegramId = (): number | null => launchTelegramId

const readString = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null)

/** Фото — только по `https`: другая схема в `<img>` не нужна. */
const readPhotoUrl = (value: unknown): string | null => {
  const url = readString(value)

  return url?.startsWith('https://') ? url : null
}

const readLaunchContext = (): LaunchContext => {
  let rawInitData: string | null = null

  try {
    rawInitData = retrieveRawInitData() ?? null
  } catch {
    // Вне Telegram без mock-env подписи нет: бэкенд ответит TG-001, это обработает оболочка.
  }

  const params = new URLSearchParams(rawInitData ?? '')
  let languageCode: string | null = null
  let telegramId: number | null = null

  try {
    const user = JSON.parse(params.get('user') ?? 'null') as {
      id?: unknown
      language_code?: unknown
      first_name?: unknown
      last_name?: unknown
      photo_url?: unknown
      username?: unknown
    } | null

    languageCode = typeof user?.language_code === 'string' ? user.language_code : null
    telegramId = typeof user?.id === 'number' && Number.isSafeInteger(user.id) && user.id > 0 ? user.id : null

    const firstName = readString(user?.first_name)

    telegramUser = firstName
      ? {
          firstName,
          lastName: readString(user?.last_name),
          photoUrl: readPhotoUrl(user?.photo_url),
          username: readString(user?.username)?.replace(/^@/, '') ?? null,
        }
      : null
  } catch {
    // Повреждённый user — язык останется по умолчанию, подпись всё равно проверит бэкенд.
  }

  launchTelegramId = telegramId

  return { rawInitData, languageCode, startParam: params.get('start_param'), telegramId }
}

/**
 * Инициализация SDK: вызывается один раз до первого рендера.
 *
 * Каждая возможность монтируется через `ifAvailable`: клиент бывает старым,
 * и отсутствие метода не должно ронять приложение — экран просто остаётся без него.
 */
export const initTelegram = (): LaunchContext => {
  try {
    init()
  } catch (error) {
    console.error('[telegram] SDK не инициализирован', error)

    return readLaunchContext()
  }

  themeParams.mount.ifAvailable()
  // --tg-theme-* на <html>: из них app/styles собирает токены.
  themeParams.bindCssVars.ifAvailable()
  // Схема клиента для производных токенов — до первого рендера, как и палитра.
  bindColorScheme()
  miniApp.mount.ifAvailable()
  backButton.mount.ifAvailable()
  mainButton.mount.ifAvailable()
  // SecondaryButton — с Mini Apps 7.10; у старого клиента экран рисует кнопку кита сам.
  secondaryButton.mount.ifAvailable()
  // Пункт «Настройки» в меню ⋮ — с Mini Apps 6.10; у старого клиента его просто нет.
  settingsButton.mount.ifAvailable()
  swipeBehavior.mount.ifAvailable()
  // Вертикальный свайп закрывает Mini App — на длинной витрине это случайные закрытия.
  swipeBehavior.disableVertical.ifAvailable()

  const viewportMount = viewport.mount.ifAvailable()

  if (viewportMount.ok) {
    viewportMount.data
      .then(() => {
        viewport.bindCssVars.ifAvailable()
        viewport.expand.ifAvailable()
      })
      .catch((error: unknown) => console.warn('[telegram] viewport не смонтирован', error))
  }

  bindBackButton()
  bindSettingsButton()
  miniApp.ready.ifAvailable()

  return readLaunchContext()
}
